// @flow strict

import * as React from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import styles from './styles.scss';

import { apiUrl } from '../../../core/api/apiConfig';
import { provisioning } from '../api';
import type { UpflowyUserData } from '../types';
import { removeUpflowyDataStorage } from '../helpers';
import { goToProfilePage } from '../../FreeTrialThankYou/helpers';
import Iframe from '../../../components/Iframe';
import LoadingScreen from '../LoadingScreen';

type PropsType = {|
  upflowyUrl: string,
  userData: UpflowyUserData,
|};

const UpflowyOnboarding = ({ upflowyUrl, userData }: PropsType) => {
  const { userEmail, customerId, signupUserId, origin } = userData;
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isSaving, setIsSaving] = React.useState(false);
  const [iframeSrc, setIframeSrc] = React.useState('');

  const goTo500ErrorPage = () => {
    window.location.href = new URL(`${apiUrl.ansaradadotcomBaseUrl}/500`);
  };

  const saveSubscription = React.useCallback(async () => {
    try {
      setIframeSrc('');
      setIsSaving(true);
      removeUpflowyDataStorage(signupUserId);

      const recaptchaAction = 'OnBoardingSaveSubscription';
      const recaptchaToken = await executeRecaptcha(recaptchaAction);

      const {
        SignupToken,
        SessionId,
        IsProfileExists,
        Status,
      } = await provisioning.createSubscription({
        customerId,
        signupUserId,
        provisioningType: 'FreeTrial',
        recaptchaAction,
        recaptchaToken,
      });

      if (Status !== 0) {
        goTo500ErrorPage();
        return;
      }

      goToProfilePage(
        {
          sessionId: SessionId,
          signupToken: SignupToken,
          isProfileExists: IsProfileExists,
          userEmail,
        },
        true,
      );
    } catch {
      goTo500ErrorPage();
    }
  }, [customerId, signupUserId]);

  const handleUpflowyEvent = React.useCallback(event => {
    if (event.origin.startsWith('https://my.upflowy.com')) {
      if (event.data && event.data.type === 'FLOW_COMPLETE') {
        saveSubscription();
      }
    }
  }, []);

  React.useEffect(() => {
    if (signupUserId && userEmail && origin) {
      setIframeSrc(
        `${upflowyUrl}?u-signupUserId=${encodeURIComponent(
          signupUserId,
        )}&u-userEmail=${encodeURIComponent(userEmail)}&mode=standalone&origin=${origin}&404=${
          apiUrl.ansaradadotcomBaseUrl
        }/404`,
      );
    }
  }, [signupUserId, userEmail, origin, upflowyUrl]);

  React.useEffect(() => {
    if (!upflowyUrl) {
      goTo500ErrorPage();
      return undefined;
    }

    window.addEventListener('message', handleUpflowyEvent);

    return () => {
      window.removeEventListener('message', handleUpflowyEvent);
    };
  }, []);

  return (
    <div id="upflowyContainer" data-testid="upflowy-container" className={styles.upflowyContainer}>
      {iframeSrc && <Iframe className={styles.fullScreen} src={iframeSrc} />}
      <LoadingScreen show={isSaving} />
    </div>
  );
};

export default UpflowyOnboarding;
